import type { SRT_SortingState } from '@/shared/components/master-table'
import type { SavedFilterModel } from '@/shared/generated'
import type { TFunction } from 'i18next'
import type { SavedReportsActionsHandlers } from '../ui/SavedReportsActionsDropdown'

import { useMemo, useState } from 'react'
import { Link } from '@tanstack/react-router'
import { CheckIcon, FileTextIcon, XIcon } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import { UsernameById } from '@/entities/user'

import {
	createSRTColumnHelper,
	useMasterReactTable,
} from '@/shared/components/master-table'
import { LinkButton } from '@/shared/components/ui/link-button'
import {
	Tooltip,
	TooltipContent,
	TooltipTrigger,
} from '@/shared/components/ui/tooltip'
import { SavedFilterType } from '@/shared/generated'
import { getMasterTableLocale } from '@/shared/i18n'
import { ampli } from '@/shared/lib/ampli'
import { intlFormat } from '@/shared/tools/dates'
import { getFiltersQueryObject } from '@/shared/tools/location'

import { SavedReportsActionsDropdown } from '../ui/SavedReportsActionsDropdown'
import { SavedReportsReportCounter } from '../ui/SavedReportsReportCounter'

export type SavedReportTableRow = SavedFilterModel & {
	is_global: boolean
}
export function useReportsTable({
	rows,
	isLoading,
	handlers,
}: {
	rows: SavedReportTableRow[]
	handlers: SavedReportsActionsHandlers
	isLoading: boolean
}) {
	const { t } = useTranslation()

	const [sorting, setSorting] = useState<SRT_SortingState>([])
	const columns = useMemo(() => getColumns(t), [])

	return useMasterReactTable<SavedReportTableRow>({
		localization: getMasterTableLocale(),
		data: rows,
		columns,
		state: {
			sorting,
			isLoading,
		},
		defaultColumn: {
			minSize: 28,
			size: Number.MAX_SAFE_INTEGER,
			maxSize: Number.MAX_SAFE_INTEGER,
		},
		positionActionsColumn: 'last',
		enableRowActions: true,
		getRowClassName: () => 'group/row',
		renderRowActions(props) {
			const { row } = props

			return (
				<div className='flex items-center justify-end space-x-1'>
					<SavedReportsActionsDropdown
						reportItem={row.original}
						{...handlers}
					/>
				</div>
			)
		},
		displayColumnDefOptions: {
			'srt-row-actions': {
				header: '',
			},
		},

		enableColumnFilters: true,
		layoutMode: 'grid',
		enableColumnActions: false,
		divideX: false,
		getRowHeight: () => 57,
		defaultRowHeight: 57,
		onSortingChange: setSorting,
	})
}

const columnHelper = createSRTColumnHelper<SavedReportTableRow>()
const getColumns = (t: TFunction) => [
	columnHelper.accessor('name', {
		size: 350,
		header: t('report_name'),
		meta: {
			label: t('report_name'),
			placeholder: t('general_search'),
			variant: 'text',
		},
		Cell: ({ cellValue, row }) => {
			const { t } = useTranslation()

			return (
				<div className='flex items-center gap-2 truncate'>
					<div className='flex size-8 shrink-0 items-center justify-center rounded-md border bg-muted text-muted-foreground'>
						<FileTextIcon className='size-4' />
					</div>
					<div className='flex flex-col justify-center gap-1 truncate'>
						<Tooltip>
							<TooltipTrigger asChild>
								<LinkButton asChild className='truncate'>
									<Link
										to={
											row.original.type === SavedFilterType.Products
												? '/p/products'
												: '/p/anchor-management/assortment'
										}
										search={getFiltersQueryObject(row.original.filters)}
										onClick={() =>
											ampli.reportsOpenReport({
												from: 'report name cell',
											})
										}
									>
										<div className='flex items-center space-x-2'>
											{cellValue}
										</div>
									</Link>
								</LinkButton>
							</TooltipTrigger>
							<TooltipContent>
								{t('action.open', { defaultValue: 'Open' })}
							</TooltipContent>
						</Tooltip>
						<span className='block truncate text-xs text-muted-foreground'>
							{t('general_last_updated')}:{' '}
							{row.original.updated_at
								? intlFormat(new Date(row.original.updated_at + 'Z'), {
										timeStyle: 'short',
										dateStyle: 'short',
									})
								: row.original.created_at
									? intlFormat(new Date(row.original.created_at + 'Z'), {
											timeStyle: 'short',
											dateStyle: 'short',
										})
									: '-'}
							{' by '}
							{row.original.updated_by ? (
								<UsernameById userId={row.original.updated_by} />
							) : (
								<UsernameById userId={row.original.created_by} />
							)}
						</span>
					</div>
				</div>
			)
		},
	}),

	columnHelper.accessor('is_public', {
		size: 100,
		header: t('app.is_global'),
		Cell: ({ cellValue }) => {
			const { t } = useTranslation()

			return cellValue ? (
				<Tooltip>
					<TooltipTrigger asChild>
						<span className='flex size-6 items-center justify-center gap-2 rounded-sm bg-green-500/10 text-xs text-green-500'>
							<CheckIcon className='size-4' />
						</span>
					</TooltipTrigger>
					<TooltipContent>
						{t('saved_reports.is_global.tooltip', {
							defaultValue:
								'This report is visible to all users in you organization',
						})}
					</TooltipContent>
				</Tooltip>
			) : (
				<Tooltip>
					<TooltipTrigger asChild>
						<span className='flex size-6 items-center justify-center gap-2 rounded-sm bg-destructive/10 text-xs text-destructive'>
							<XIcon className='size-4' />
						</span>
					</TooltipTrigger>
					<TooltipContent>
						{t('saved_reports.private.tooltip', {
							defaultValue: 'This report is visible only to you',
						})}
					</TooltipContent>
				</Tooltip>
			)
		},
		meta: {
			label: t('app.is_global'),
			variant: 'select',
			options: [
				{
					label: t('yes', { defaultValue: 'Yes' }),
					value: true,
				},
				{
					label: t('no', { defaultValue: 'No' }),
					value: false,
				},
			],
		},
	}),
	columnHelper.accessor('type', {
		size: 170,
		header: t('app.type'),
		Cell: ({ cellValue }) => (
			<span className='text-sm'>{t(`report.type.${cellValue}`)}</span>
		),
	}),
	columnHelper.accessor('filters', {
		header: t('app.products'),
		Cell: ({ cellValue, row }) => {
			const filters = cellValue

			return (
				<SavedReportsReportCounter
					filters={filters || []}
					type={row.original.type}
				/>
			)
		},
	}),
]
